/** A permanent identity and provenance projection; trading results are platform receipts, never a second book. */
import { z } from 'zod'
import { IsoTimestamp, Uuid, WeiString } from '../primitives.js'
export const PassportQuery = z.object({
  section: z.enum(['all', 'events', 'strategies']).default('all'),
  before: z
    .string()
    .regex(/^[1-9][0-9]{0,18}$/)
    .refine((v) => /^[1-9][0-9]{0,18}$/.test(v) && BigInt(v) <= 9223372036854775807n, 'invalid bigint cursor')
    .optional(),
  strategyBefore: z
    .string()
    .regex(/^[1-9][0-9]{0,18}$/)
    .refine((v) => /^[1-9][0-9]{0,18}$/.test(v) && BigInt(v) <= 9223372036854775807n, 'invalid bigint cursor')
    .optional(),
})
export const PassportEvent = z.object({
  id: z.string(),
  kind: z.enum(['born', 'identity_changed', 'lifecycle_changed', 'version_created', 'version_activated', 'version_changed', 'job_failed']),
  occurredAt: IsoTimestamp,
  recordedAt: IsoTimestamp,
  facts: z.record(z.string(), z.unknown()),
})
export const PassportBook = z.object({
  filled: z.number().int(),
  refused: z.number().int(),
  netWei: z
    .string()
    .regex(/^-?(0|[1-9][0-9]*)$/)
    .nullable(),
  gasWei: WeiString,
  feesWei: WeiString,
  openLots: z.number().int().nullable(),
})
export const PassportStrategy = z.object({
  strategyId: z.string(),
  firstRecordedAt: IsoTimestamp,
  asOf: IsoTimestamp.nullable(),
  paper: PassportBook.nullable(),
  live: PassportBook.nullable(),
  unavailable: z.string().nullable(),
  source: z.enum(['platform', 'retained', 'unavailable']),
})
export const AgentPassport = z.object({
  agentId: Uuid,
  handle: z.string(),
  name: z.string(),
  status: z.string(),
  bornAt: IsoTimestamp,
  recordedSince: IsoTimestamp,
  historyComplete: z.boolean(),
  versions: z.number().int(),
  modelChargedMicro: WeiString,
  providerCostMicro: WeiString,
  recordedFailures: z.number().int(),
  ancestry: z.array(z.object({ parentAgentId: Uuid, parentStrategyId: z.string().nullable(), at: IsoTimestamp })),
  events: z.array(PassportEvent),
  nextBefore: z.string().nullable(),
  strategies: z.array(PassportStrategy),
  nextStrategyBefore: z.string().nullable(),
  asOf: IsoTimestamp,
})
export type AgentPassport = z.output<typeof AgentPassport>
